// Pure optimistic reducer: displayed state = server state + pending outbox ops (STORE03). The server stays authoritative.
import type { Progress, StateOperation, UserState } from '@tuned/contracts';
import { isAutoCompleted } from './progress.ts';

function upsert(list: Progress[], p: Progress): Progress[] {
  return list.some((x) => x.itemId === p.itemId) ? list.map((x) => (x.itemId === p.itemId ? p : x)) : [...list, p];
}

export function applyOp(s: UserState, op: StateOperation, at: string): UserState {
  switch (op.op) {
    case 'list.add':
      if (s.lists.some((l) => l.list === op.list && l.itemId === op.itemId)) return s;
      return { ...s, lists: [...s.lists, { list: op.list, itemId: op.itemId, kind: op.kind, addedAt: at, available: true }] };
    case 'list.remove':
      return { ...s, lists: s.lists.filter((l) => !(l.list === op.list && l.itemId === op.itemId)) };
    case 'progress.update': {
      const cur = s.progress.find((p) => p.itemId === op.itemId);
      const sameSession = !!cur && cur.sessionId === op.sessionId;
      if (cur && sameSession && cur.seq >= op.seq) return s; // older event of the same session
      const manual = cur?.manual ?? null;
      const auto = (sameSession && manual === null && !!cur?.completed) || isAutoCompleted(op.positionSec, op.durationSec);
      return {
        ...s,
        progress: upsert(s.progress, {
          itemId: op.itemId, positionSec: op.positionSec, durationSec: op.durationSec, completed: manual ?? auto,
          manual, sessionId: op.sessionId, seq: op.seq, updatedAt: at,
        }),
      };
    }
    case 'progress.remove':
      return { ...s, progress: s.progress.filter((p) => p.itemId !== op.itemId) };
    case 'watched.set': {
      const cur = s.progress.find((p) => p.itemId === op.itemId);
      if (!cur && op.watched === null) return s;
      const base: Progress = cur ?? { itemId: op.itemId, positionSec: 0, durationSec: null, completed: false, manual: null, sessionId: null, seq: 0, updatedAt: at };
      const completed = op.watched ?? isAutoCompleted(base.positionSec, base.durationSec);
      return { ...s, progress: upsert(s.progress, { ...base, manual: op.watched, completed, updatedAt: at }) };
    }
    case 'reminder.add':
      return s; // needs a server-side program lookup; shown after sync
    case 'reminder.remove':
      return { ...s, reminders: s.reminders.filter((r) => r.id !== op.reminderId) };
    case 'preferences.set': {
      const set = Object.fromEntries(Object.entries(op.preferences).filter(([, v]) => v !== undefined));
      return { ...s, preferences: { ...s.preferences, ...set } };
    }
    case 'history.clear':
      return { ...s, progress: [] };
  }
}
